// =============================================================================
// scopeCascade.ts — Patch 015.1 frontend API client for subtractive cascade.
// =============================================================================

import { request } from './request'
import type {
  CascadeLatestResponse,
  CascadeRunResponse,
  CompletenessHistoryResponse,
} from './types'

export const scopeCascadeApi = {
  run: (projectId: string, notes?: string) =>
    request<CascadeRunResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/scope-cascade/run`,
      {
        method: 'POST',
        body: JSON.stringify(notes !== undefined ? { notes } : {}),
      },
    ),

  latest: (projectId: string) =>
    request<CascadeLatestResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/scope-cascade/latest`,
    ),

  // Wave-7 D3 — read-only master-scope completeness-check history
  // (GET /scope/completeness-history, Patch 012.D). Trend-surfaced only.
  completenessHistory: (projectId: string) =>
    request<CompletenessHistoryResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/scope/completeness-history`,
    ),

  // Patch 015.40-W1.22 — manual residual decision. Upsert by
  // (project_id, universe_item_id). Caller refetches the cascade
  // (.run or .latest) afterwards so the included/excluded/residual
  // counts reflect the overlay.
  decide: (
    projectId: string,
    body: { universe_item_id: string; decision: 'include' | 'exclude'; notes?: string },
  ) =>
    request<{ ok: true; id: string; outcome: 'inserted' | 'updated'; decision: 'include' | 'exclude' }>(
      `/api/projects/${encodeURIComponent(projectId)}/scope-cascade/decisions`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // Patch 015.40-W1.23 — AI suggestions for residuals. Sonnet 4.6 reads
  // each residual's rule + the project facts and proposes include/exclude
  // with a one-sentence reason. Suggestions are TRANSIENT -- nothing
  // persists until the operator clicks Accept (which fires .decide above).
  //
  // Patch 015.40-W1.23.1 -- `partial_recovery` flag set when the model
  // truncated mid-array and we salvaged the complete items before the
  // cutoff. Caller surfaces a notice + offers Re-run AI.
  suggestResiduals: (projectId: string) =>
    request<{
      ok: true
      suggestions: Array<{
        universe_item_id: string
        suggestion: 'include' | 'exclude'
        reason: string
      }>
      partial_recovery?: boolean
      failed_batches?: number
      total_batches?: number
      metadata: {
        prompt_version: string
        model: string
        token_usage_input: number
        token_usage_output: number
        cost_estimate_usd: number
        latency_ms: number
      }
    }>(
      `/api/projects/${encodeURIComponent(projectId)}/scope-cascade/suggest-residuals`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
}
